import React, { useState, useRef } from 'react';
import { Camera, Upload, Trash2, Download, FileText, Image as ImageIcon, X, Smartphone } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API, withToken } from '@/utils/api';
import axios from 'axios';

export const AttachmentsSection = ({ woId, workOrder, isAtelier }) => {
  const attachments = workOrder?.attachments || [];
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanSession, setScanSession] = useState(null);
  const scanPollRef = useRef(null);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/work-orders/${woId}/attachments`, fd);
      }
      toast.success(`${files.length} fichier(s) ajoute(s)`);
      window.location.reload();
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (attId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API}/work-orders/${woId}/attachments/${attId}`);
      toast.success('Piece jointe supprimee');
      window.location.reload();
    } catch { toast.error('Erreur suppression'); }
  };

  const isImage = (ct) => ct?.startsWith('image/');

  return (
    <Card className="p-4 rounded-xl space-y-3" data-testid="attachments-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Camera className="w-4 h-4" /> Photos
          {attachments.length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-mono">{attachments.length}</span>
          )}
        </h3>
        {workOrder?.status !== 'completed' && (
          <div className="flex items-center gap-1.5">
            <label className="cursor-pointer">
              <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleUpload} data-testid="wo-attachment-input" />
              <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-md transition-colors cursor-pointer font-medium">
                <Upload className="w-4 h-4" /> {uploading ? 'Envoi...' : 'Ajouter une photo'}
              </span>
            </label>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors font-medium"
              data-testid="wo-scan-qr-btn"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/attachment-scan-sessions`, { context: 'work_order', context_id: woId });
                  setScanSession(res.data);
                  if (scanPollRef.current) clearInterval(scanPollRef.current);
                  scanPollRef.current = setInterval(async () => {
                    try {
                      const poll = await axios.get(`${API}/attachment-scan-sessions/${res.data.id}`);
                      if (poll.data.files?.length > 0) {
                        // Auto-attach files to work order
                        for (const f of poll.data.files) {
                          if (!f._attached) {
                            // Copy from scan session to WO attachments
                            await axios.post(`${API}/work-orders/${woId}/attachments/from-scan`, { file_id: f.id, stored_filename: f.stored_filename, filename: f.filename, content_type: f.content_type, size: f.size, gridfs_id: f.gridfs_id || null });
                            f._attached = true;
                          }
                        }
                        toast.success('Photo(s) recue(s) du telephone');
                        clearInterval(scanPollRef.current);
                        setScanSession(null);
                        window.location.reload();
                      }
                    } catch { /* ignore */ }
                  }, 2000);
                } catch { toast.error('Erreur creation session'); }
              }}
            >
              <Smartphone className="w-4 h-4" /> Photo avec le téléphone (QR)
            </button>
          </div>
        )}
      </div>

      {/* QR Scan session display */}
      {scanSession && (
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-center">
          <p className="text-sm font-medium text-slate-700 mb-2">Scannez ce QR code avec votre telephone</p>
          <img src={scanSession.qr_code} alt="QR Code" className="w-36 h-36 mx-auto" data-testid="wo-attachment-qr" />
          <p className="text-xs text-slate-500 mt-2 animate-pulse">En attente des photos...</p>
          <button type="button" className="text-xs text-slate-500 hover:text-slate-700 underline mt-2"
            onClick={() => { if (scanPollRef.current) clearInterval(scanPollRef.current); setScanSession(null); }}
            data-testid="wo-close-qr-scan">Fermer</button>
        </div>
      )}

      {attachments.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">Aucune photo — scannez le QR code avec votre téléphone pour en prendre une</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {attachments.map(att => (
            <div key={att.id} className="group relative bg-white border rounded-lg overflow-hidden" data-testid={`attachment-${att.id}`}>
              {isImage(att.content_type) ? (
                <img
                  src={withToken(`${API}/work-orders/${woId}/attachments/${att.id}`)}
                  alt={att.filename}
                  className="w-full h-28 object-cover cursor-pointer"
                  onClick={() => setPreviewUrl(withToken(`${API}/work-orders/${woId}/attachments/${att.id}`))}
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; const fallback = document.createElement('div'); fallback.className = 'w-full h-28 flex items-center justify-center bg-red-50 text-red-300 text-xs'; fallback.textContent = 'Image indisponible'; e.target.parentElement.appendChild(fallback); }}
                />
              ) : (
                <div className="w-full h-28 flex items-center justify-center bg-slate-50">
                  <FileText className="w-8 h-8 text-slate-300" />
                </div>
              )}
              <div className="p-2 flex items-center gap-1">
                <span className="text-xs truncate flex-1 text-slate-600">{att.filename}</span>
                <a href={withToken(`${API}/work-orders/${woId}/attachments/${att.id}`)} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700">
                  <Download className="w-3.5 h-3.5" />
                </a>
                {!isAtelier && (
                  <button onClick={() => handleDelete(att.id)} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8" onClick={() => setPreviewUrl(null)}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setPreviewUrl(null)}>
            <X className="w-8 h-8" />
          </button>
          <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </Card>
  );
};
